import { useS3UploadMutation } from 'queries/aws/useS3UploadMutation';
import { useSignedUrlVideoReplyMutation } from 'queries/videoReply/useSignedUrlVideoReplyMutation';
import { useVideoReplyCreateVideoMutation } from 'queries/videoReply/useVideoReplyCreateVideoMutation';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { IUploadVideoFormikValues } from './ModalUploadRecordVideo';
import { RECORD_LOADER_STATUS } from './types';
export const CONTENT_TYPE = 'video/webm';

interface IUseRecordUploadFormDataProps extends IUploadVideoFormikValues {
  file: File;
}
interface IProps {
  handleModalClose: () => void;
}

export const useVideoRecordUpload = ({ handleModalClose }: IProps) => {
  const { shortUrl, eridEncoded } = useParams() as {
    shortUrl: string;
    eridEncoded: string;
  };
  const [progressState, setProgressState] = useState(
    RECORD_LOADER_STATUS.UPLOADING
  );
  const [loadingStarted, setLoadingIsStared] = useState(false);
  const [recordingFormData, setRecordingFormData] =
    useState<IUseRecordUploadFormDataProps | null>(null);
  const {
    mutateAsync: createVideoReplySignedUrl,
    isLoading: isLoadingUrl,
    data: presignedUrlData,
    error: urlErrorData,
    isSuccess,
  } = useSignedUrlVideoReplyMutation();

  const {
    mutateAsync: uploadVideoToS3,
    isLoading: isUploading,
    error: uploadError,
    progress: uploadToS3Progress,
  } = useS3UploadMutation();

  const {
    mutateAsync: createVideo,
    isLoading: isCreating,
    error: createError,
    progress: createProgress,
  } = useVideoReplyCreateVideoMutation();

  useEffect(() => {
    if (isSuccess) {
      startUpload();
    }
  }, [isSuccess]);

  const startUpload = async () => {
    setLoadingIsStared(true);
    try {
      if (presignedUrlData?.url && recordingFormData) {
        // Upload to S3
        setProgressState(RECORD_LOADER_STATUS.UPLOADING);
        await uploadVideoToS3({
          url: presignedUrlData.url,
          file: recordingFormData.file,
          contentType: CONTENT_TYPE,
        });

        // Create video
        setProgressState(RECORD_LOADER_STATUS.CREATE);
        await createVideo({
          filename: recordingFormData.file.name,
          eridEncoded,
          shortUrl,
          email: recordingFormData.email,
          name: recordingFormData.name,
          message: recordingFormData?.message,
        });
        // Final upload completion state
        setProgressState(RECORD_LOADER_STATUS.UPLOAD_COMPLETED);
        handleModalClose();
        setLoadingIsStared(false);
      }
    } catch (error) {
      console.error('Error during upload:', error);
      setProgressState(RECORD_LOADER_STATUS.ERROR);
      setLoadingIsStared(false);
    }
  };
  const isLoading = isLoadingUrl || isUploading || isCreating || loadingStarted;
  const isError = uploadError || createError || urlErrorData;
  return {
    createVideoReplySignedUrl,
    setRecordingFormData,
    isLoading,
    progressState,
    uploadToS3Progress,
    createProgress,
    isError,
  };
};
